// Auth.jsx - Updated 2026-04-05 for registration mode fix
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { API_URL } from '../utils/api';
import { LeafIcon } from '../components/ui/Icons';

const Auth = () => {
    const [isLogin, setIsLogin] = useState(true);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '', color: 'bg-gray-200' });
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [location, setLocation] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [oauthLoading, setOauthLoading] = useState({ google: false, apple: false });
    // forgotPassword mode
    const [forgotPassword, setForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetSent, setResetSent] = useState(false);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const navigate = useNavigate();
    const { login } = useContext(AuthContext);
    const { showToast } = useToast();

    // Check for registration mode in URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('mode') === 'register') {
            setIsLogin(false);
        }
    }, []);

    const calculateStrength = (pwd) => {
        if (!pwd) return { score: 0, label: '', color: 'bg-gray-200' };
        
        let score = 0;
        if (pwd.length >= 8) score++;
        if (/[0-9]/.test(pwd)) score++;
        if (/[^A-Za-z0-9]/.test(pwd)) score++;
        if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;

        const maps = [
            { score: 0, label: 'Zu kurz', color: 'bg-red-400' },
            { score: 1, label: 'Schwach', color: 'bg-orange-400' },
            { score: 2, label: 'Mittel', color: 'bg-yellow-400' },
            { score: 3, label: 'Gut', color: 'bg-blue-400' },
            { score: 4, label: 'Stark', color: 'bg-plantlix-500' }
        ];
        
        return maps[score];
    };

    const handlePasswordChange = (e) => {
        const val = e.target.value;
        setPassword(val);
        setPasswordStrength(calculateStrength(val));
    };

    // OAuth Handlers - Seamless Integration
    const handleGoogleAuth = async () => {
        setOauthLoading(prev => ({ ...prev, google: true }));
        setError('');

        try {
            // Fetch Google OAuth config from backend
            const configResponse = await fetch(`${API_URL}/api/oauth/providers/`);
            const config = await configResponse.json().catch(() => ({}));

            const clientId = config.google?.client_id || '';
            if (!clientId) {
                setOauthLoading(prev => ({ ...prev, google: false }));
                setError('Google OAuth nicht konfiguriert.');
                return;
            }

            // Wait for Google library to load
            let attempts = 0;
            const checkGoogle = setInterval(() => {
                attempts++;
                if (typeof window.google !== 'undefined' && window.google.accounts) {
                    clearInterval(checkGoogle);

                    // Initialize Google Identity Services
                    window.google.accounts.id.initialize({
                        client_id: clientId,
                        callback: async (response) => {
                            try {
                                const oauthResponse = await fetch(`${API_URL}/api/oauth/google/`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ id_token: response.credential })
                                });

                                if (!oauthResponse.ok) {
                                    const errorData = await oauthResponse.json().catch(() => ({}));
                                    throw new Error(errorData.error || 'Google-Anmeldung fehlgeschlagen');
                                }

                                const data = await oauthResponse.json();
                                login(data.access, data.refresh);
                                showToast('Mit Google angemeldet! 🎉', 'success');
                                navigate('/dashboard');
                                setOauthLoading(prev => ({ ...prev, google: false }));
                            } catch (err) {
                                setError(`Google OAuth: ${err.message}`);
                                setOauthLoading(prev => ({ ...prev, google: false }));
                            }
                        }
                    });

                    // Show One Tap prompt
                    window.google.accounts.id.prompt();

                    // Also render button as fallback
                    const btn = document.getElementById('google-signin-button');
                    if (btn) {
                        window.google.accounts.id.renderButton(btn, {
                            theme: 'outline',
                            size: 'large',
                            width: '100%',
                            text: 'signin_with'
                        });
                    }
                } else if (attempts > 20) {
                    clearInterval(checkGoogle);
                    console.warn('Google Sign-In library failed to load');
                    setOauthLoading(prev => ({ ...prev, google: false }));
                }
            }, 100);
        } catch (err) {
            console.error('Google OAuth error:', err);
            setOauthLoading(prev => ({ ...prev, google: false }));
        }
    };

    const handleAppleAuth = async () => {
        setOauthLoading(prev => ({ ...prev, apple: true }));
        setError('');

        try {
            // Hole Config aus dem Backend
            const configResponse = await fetch(`${API_URL}/api/oauth/providers/`);
            const config = await configResponse.json().catch(() => ({}));
            
            const serviceId = config.apple?.service_id || '';
            const redirectUri = config.apple?.redirect_uri || '';
            
            if (!serviceId) {
                setOauthLoading(prev => ({ ...prev, apple: false }));
                setError('Apple Sign-In Option im Backend noch nicht konfiguriert.');
                return;
            }

            // Lade Apple JS SDK dynamisch
            if (typeof window.AppleID === 'undefined') {
                await new Promise((resolve, reject) => {
                    const script = document.createElement('script');
                    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
                    script.onload = resolve;
                    script.onerror = () => reject(new Error('Apple Script konnte nicht geladen werden'));
                    document.head.appendChild(script);
                });
            }

            // Init Apple
            window.AppleID.auth.init({
                clientId: serviceId,
                scope: 'name email',
                redirectURI: redirectUri,
                usePopup: true
            });

            // Trigger den SignIn (startet Popup)
            const data = await window.AppleID.auth.signIn();
            
            const id_token = data.authorization.id_token;
            // Name Property ist NUR beim allereigensten ersten Login vorhanden
            const firstName = data.user?.name?.firstName || '';
            const lastName = data.user?.name?.lastName || '';

            // Sende ans Backend
            const oauthResponse = await fetch(`${API_URL}/api/oauth/apple/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    id_token,
                    first_name: firstName,
                    last_name: lastName
                })
            });

            if (!oauthResponse.ok) {
                const errorData = await oauthResponse.json().catch(() => ({}));
                throw new Error(errorData.error || 'Server-Validierung für Apple fehlgeschlagen');
            }

            const authData = await oauthResponse.json();
            login(authData.access, authData.refresh);
            showToast('Mit Apple angemeldet! 🍏', 'success');
            navigate('/dashboard');
        } catch (err) {
            console.error('Apple OAuth error:', err);
            // Ignoriere Error wenn Popup vom User geschlossen wurde
            if (err.error !== 'popup_closed_by_user') {
                setError(`Apple Login: ${err.message || 'Abgebrochen'}`);
            }
        } finally {
            setOauthLoading(prev => ({ ...prev, apple: false }));
        }
    };


    // Check for OAuth callback on mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');

        if (error) {
            setError(`OAuth-Fehler: ${error}`);
            return;
        }

    }, [login]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        const endpoint = isLogin ? 'token/' : 'register/';
        const payload = isLogin 
            ? { username, password } 
            : { username, password, email, first_name: firstName, last_name: lastName, location };

        try {
            const response = await fetch(`${API_URL}/api/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const text = await response.text();
            let data;
            try {
                data = JSON.parse(text);
            } catch (jsonError) {
                // If it's not JSON, use the raw text for the error message
                data = { detail: text };
            }

            if (!response.ok) {
                const errorMsg = data.detail || data.error || (typeof data === 'object' ? Object.values(data).flat().join(' ') : String(text));
                throw new Error(errorMsg || `Server-Fehler (${response.status})`);
            }
            if (isLogin) {
                login(data.access, data.refresh);
                showToast(`Willkommen zurück, ${username}! 🌿`, 'success');
                navigate('/dashboard');
            } else {
                setRegistrationSuccess(true);
                showToast(`Account erstellt! Bitte bestätige deine E-Mail, ${username}! 📧`, 'success');
            }
        } catch (err) {
            setError(`Fehler: ${err.message}`);
            showToast(err.message, 'error');
        } finally {
            setLoading(false);
        }
    };
    const handleResendEmail = async () => {
        setResendLoading(true);
        try {
            // Correct API path: /api/verify-email/resend/
            const response = await fetch(`${API_URL}/api/verify-email/resend/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            
            // Robust check for non-JSON content types (e.g. 404 HTML)
            const contentType = response.headers.get("content-type");
            let data;
            if (contentType && contentType.indexOf("application/json") !== -1) {
                data = await response.json();
            } else {
                const text = await response.text();
                throw new Error(`Server-Antwort ist kein JSON (${response.status}).`);
            }

            if (!response.ok) throw new Error(data.error || data.detail || 'Fehler beim Senden');
            showToast('Bestätigungs-E-Mail wurde erneut versendet! 🌿', 'success');
        } catch (err) {
            console.error("Resend error:", err);
            showToast(err.message, 'error');
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <div className="relative min-h-screen overflow-hidden flex items-center justify-center bg-white dark:bg-[#0d0f12] py-12 px-4 sm:px-6">
            {/* Soft Animated Background Gradients */}
            <div className="absolute inset-0 z-0">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-plantlix-200/40 rounded-full blur-[120px] animate-pulse"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[45%] h-[45%] bg-plantlix-200/30 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }}></div>
                <div className="absolute top-[20%] right-[5%] w-[30%] h-[30%] bg-lime-100/40 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '4s' }}></div>
            </div>
            <div className="pointer-events-none absolute inset-0">
                <svg
                    viewBox="0 0 320 320"
                    className="absolute -top-24 -left-20 w-72 h-72 opacity-60"
                    style={{ animation: 'leafFloat1 18s ease-in-out infinite' }}
                    aria-hidden="true"
                >
                    <defs>
                        <linearGradient id="mountainGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#d7f2df" />
                            <stop offset="100%" stopColor="#bfe8c9" />
                        </linearGradient>
                    </defs>
                    <path d="M0 260 L90 110 L160 220 L220 140 L320 260 Z" fill="url(#mountainGradient)" />
                    <path d="M40 260 Q90 190 140 260" stroke="#cfead5" strokeWidth="18" strokeLinecap="round" fill="none" />
                    <circle cx="230" cy="120" r="32" fill="#dff4e7" opacity="0.6" />
                </svg>

                <svg
                    viewBox="0 0 240 240"
                    className="absolute bottom-[-80px] right-[-60px] w-80 h-80 opacity-55"
                    style={{ animation: 'leafFloat3 20s ease-in-out infinite' }}
                    aria-hidden="true"
                >
                    <path d="M20 220 C40 140 80 60 120 40 C180 10 210 100 220 180 Z" fill="#cbeed7" />
                    <path d="M80 210 C120 120 150 90 200 110" stroke="#9fd8aa" strokeWidth="14" strokeLinecap="round" fill="none" />
                    <circle cx="70" cy="150" r="18" fill="#b6e3c4" />
                    <circle cx="165" cy="75" r="12" fill="#b6e3c4" />
                </svg>

                <svg
                    viewBox="0 0 200 200"
                    className="absolute top-1/3 right-[12%] w-48 h-48 opacity-50"
                    style={{ animation: 'leafFloat2 16s ease-in-out infinite' }}
                    aria-hidden="true"
                >
                    <path d="M40 160 C40 100 120 40 170 70 C210 95 190 160 120 180 Z" fill="#d9f3e2" />
                    <path d="M58 150 Q120 120 148 150" stroke="#a4dcb3" strokeWidth="12" strokeLinecap="round" fill="none" />
                </svg>
            </div>

            <div className="relative z-10 w-full max-w-4xl">
                <div className="max-w-md mx-auto relative group">
                    {/* Glow effect behind the card */}
                    <div className="absolute -inset-1 bg-gradient-to-r from-plantlix-400 to-plantlix-500 rounded-3xl blur opacity-20 group-hover:opacity-30 transition duration-1000"></div>
                    
                    <div className="card p-8 sm:p-10 bg-white/70 dark:bg-gray-900/60 backdrop-blur-xl border border-white/20 dark:border-gray-700/30 shadow-2xl rounded-3xl overflow-hidden relative">
                        {/* Decorative leaf shimmer */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-plantlix-500/5 blur-[60px] rounded-full -mr-16 -mt-16"></div>
                    <div className="text-center mb-8 relative z-10">
                        <div className="mb-4 inline-flex items-center justify-center p-3 bg-plantlix-50 dark:bg-plantlix-900/20 rounded-2xl">
                            <LeafIcon color="#16a34a" className="w-10 h-10" />
                        </div>
                        {registrationSuccess ? (
                            <>
                                <h2 className="text-3xl font-extrabold text-gray-800 dark:text-gray-100 tracking-tight">Fast geschafft! 🌿</h2>
                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                    Wir haben dir eine Bestätigungs-E-Mail an <strong>{email}</strong> gesendet.
                                </p>
                            </>
                        ) : (
                            <>
                                <h2 className="text-3xl font-extrabold text-gray-800 dark:text-gray-100 tracking-tight">
                                    {isLogin ? 'Willkommen zurück!' : 'Werde Teil von Plantlix'}
                                </h2>
                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                    {isLogin ? 'Melde dich an, um dein grünes Wissen zu erweitern.' : 'Starte deine botanische Reise mit uns.'}
                                </p>
                            </>
                        )}
                    </div>

                    {error && (
                        <div className="bg-red-50/90 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-100 dark:border-red-900/50 p-4 rounded-xl text-center mb-6 text-sm flex items-center justify-center gap-2 animate-in fade-in slide-in-from-top-1">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            {error}
                        </div>
                    )}

                    {/* Classic Login/Register Form FIRST */}
                        {registrationSuccess ? (
                            <div className="py-6 space-y-6">
                                <div className="flex flex-col items-center gap-4 text-center">
                                    <div className="w-20 h-20 bg-plantlix-50 dark:bg-plantlix-900/20 rounded-full flex items-center justify-center text-4xl animate-bounce">
                                        📬
                                    </div>
                                    <div className="space-y-2">
                                        <p className="text-gray-600 dark:text-gray-300 text-sm">
                                            Bitte klicke auf den Link in der E-Mail, um deinen Account zu aktivieren. Danach kannst du dich ganz normal anmelden.
                                        </p>
                                        <p className="text-[10px] text-gray-400">
                                            (Schau auch in deinen Spam-Ordner)
                                        </p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <button
                                        onClick={() => { setRegistrationSuccess(false); setIsLogin(true); setError(''); }}
                                        className="w-full py-4 px-4 rounded-xl shadow-lg shadow-plantlix-500/10 text-base font-bold text-plantlix-700 bg-plantlix-50 hover:bg-plantlix-100 dark:bg-plantlix-900/30 dark:text-plantlix-400 dark:hover:bg-plantlix-900/40 transition-all active:scale-95"
                                    >
                                        Zum Login zurückkehren
                                    </button>
                                    
                                    <div className="text-center">
                                        <button
                                            onClick={handleResendEmail}
                                            disabled={resendLoading}
                                            className="text-xs font-semibold text-gray-500 hover:text-plantlix-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
                                        >
                                            {resendLoading ? (
                                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-gray-300 border-t-gray-500"></div>
                                            ) : 'Code nicht erhalten? Erneut senden'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <form className="space-y-5 relative z-10" onSubmit={handleSubmit}>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider ml-1">Benutzername</label>
                                    <input
                                        type="text"
                                        required
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        className="w-full px-4 py-3.5 rounded-xl border border-gray-200/50 dark:border-gray-700/50 bg-white/50 dark:bg-black/20 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-plantlix-500/50 transition-all shadow-sm placeholder:text-gray-400 dark:text-white"
                                        placeholder="Dein Benutzername"
                                    />
                                </div>

                                {!isLogin && (
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider ml-1">E-Mail</label>
                                        <input
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full px-4 py-3.5 rounded-xl border border-gray-200/50 dark:border-gray-700/50 bg-white/50 dark:bg-black/20 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-plantlix-500/50 transition-all shadow-sm placeholder:text-gray-400 dark:text-white"
                                            placeholder="deine.email@beispiel.de"
                                        />
                                    </div>
                                )}

                                {!isLogin && (
                                    <>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider ml-1">Vorname (opt.)</label>
                                                <input
                                                    type="text"
                                                    value={firstName}
                                                    onChange={(e) => setFirstName(e.target.value)}
                                                    className="w-full px-4 py-3.5 rounded-xl border border-gray-200/50 dark:border-gray-700/50 bg-white/50 dark:bg-black/20 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-plantlix-500/50 transition-all dark:text-white"
                                                    placeholder="Max"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider ml-1">Nachname (opt.)</label>
                                                <input
                                                    type="text"
                                                    value={lastName}
                                                    onChange={(e) => setLastName(e.target.value)}
                                                    className="w-full px-4 py-3.5 rounded-xl border border-gray-200/50 dark:border-gray-700/50 bg-white/50 dark:bg-black/20 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-plantlix-500/50 transition-all dark:text-white"
                                                    placeholder="Mustermann"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider ml-1">Wohnort/Region (opt.)</label>
                                            <input
                                                type="text"
                                                value={location}
                                                onChange={(e) => setLocation(e.target.value)}
                                                className="w-full px-4 py-3.5 rounded-xl border border-gray-200/50 dark:border-gray-700/50 bg-white/50 dark:bg-black/20 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-plantlix-500/50 transition-all dark:text-white"
                                                placeholder="z.B. Sachsen, Berlin..."
                                            />
                                            <div className="flex gap-2 p-3 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-100/50 dark:border-amber-800/30 rounded-xl mt-2">
                                                <span className="text-lg">💡</span>
                                                <p className="text-[11px] text-amber-700 dark:text-amber-400 leading-tight">
                                                    <strong>Hinweis:</strong> Diese Angaben sind freiwillig, werden aber für den korrekten Ausdruck deiner <strong>Zertifikate & Urkunden</strong> benötigt.
                                                </p>
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider ml-1">Passwort</label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            required
                                            value={password}
                                            onChange={handlePasswordChange}
                                            className="w-full px-4 py-3.5 rounded-xl border border-gray-200/50 dark:border-gray-700/50 bg-white/50 dark:bg-black/20 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-plantlix-500/50 transition-all shadow-sm pr-11 placeholder:text-gray-400 dark:text-white"
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-plantlix-600 transition-colors bg-transparent border-none cursor-pointer focus:outline-none"
                                        >
                                            {showPassword ? (
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                                            ) : (
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                            )}
                                        </button>
                                    </div>
                                    
                                    {password && !isLogin && (
                                        <div className="mt-3 px-1 animate-in fade-in duration-500">
                                            <div className="flex justify-between items-center mb-1.5">
                                                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">Sicherheits-Check: <span className={passwordStrength.color.replace('bg-', 'text-')}>{passwordStrength.label}</span></span>
                                            </div>
                                            <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden flex gap-1">
                                                {[1, 2, 3, 4].map((step) => (
                                                    <div 
                                                        key={step} 
                                                        className={`h-full flex-1 transition-all duration-700 ${step <= passwordStrength.score ? passwordStrength.color : 'bg-transparent'}`}
                                                    ></div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full flex justify-center py-4 px-4 rounded-xl shadow-lg shadow-plantlix-500/20 text-base font-bold text-white bg-gradient-to-r from-plantlix-600 to-plantlix-600 hover:from-plantlix-500 hover:to-plantlix-500 transform transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed mt-2 group"
                                >
                                    {loading ? (
                                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            {isLogin ? 'Anmelden' : 'Jetzt registrieren'}
                                            <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                        </span>
                                    )}
                                </button>

                                {isLogin && (
                                    <div className="text-center">
                                        <button
                                            type="button"
                                            onClick={() => { setForgotPassword(true); setError(''); }}
                                            className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-plantlix-600 dark:hover:text-plantlix-400 transition-colors"
                                        >
                                            Passwort vergessen?
                                        </button>
                                    </div>
                                )}
                            </form>
                        )}

                    <div className="relative my-8">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-100 dark:border-gray-800"></div>
                        </div>
                        <div className="relative flex justify-center text-[10px] font-bold uppercase tracking-widest">
                            <span className="px-4 bg-transparent text-gray-400 backdrop-blur-md">Social Login</span>
                        </div>
                    </div>

                    {/* Social Buttons BELOW Form */}
                    <div className="grid grid-cols-2 gap-3 mb-8">
                        <button
                            type="button"
                            onClick={handleGoogleAuth}
                            disabled={oauthLoading.google || loading}
                            className="flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-all font-semibold text-sm shadow-sm"
                        >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                            </svg>
                            <span>Google</span>
                        </button>
                        
                        <button
                            type="button"
                            onClick={handleAppleAuth}
                            disabled={oauthLoading.apple || loading}
                            className="flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-all font-semibold text-sm shadow-sm"
                        >
                            <svg className="w-4 h-4" viewBox="0 0 384 512" fill="currentColor">
                                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                            </svg>
                            <span>Apple</span>
                        </button>
                    </div>

                    <div className="text-center pt-2 border-t border-gray-100 dark:border-gray-800">
                        <button
                            onClick={() => { setIsLogin(!isLogin); setError(''); }}
                            className="text-sm font-bold text-plantlix-600 dark:text-plantlix-400 hover:text-plantlix-700 dark:hover:text-plantlix-300 transition-colors"
                        >
                            {isLogin ? 'Neugierig? Jetzt kostenlos registrieren' : 'Bereits dabei? Hier anmelden'}
                        </button>
                    </div>

                    {/* GUEST ACCESS - De-emphasized at the very bottom */}
                    <div className="mt-10 pt-4 flex flex-col items-center gap-4">
                        <div className="flex items-center gap-4 w-full">
                            <div className="h-px bg-gray-100 dark:bg-gray-800 flex-1"></div>
                            <span className="text-[10px] text-gray-400 font-medium">Noch unsicher?</span>
                            <div className="h-px bg-gray-100 dark:bg-gray-800 flex-1"></div>
                        </div>
                        <button
                            type="button"
                            onClick={async () => {
                                setError('');
                                setLoading(true);
                                try {
                                    const response = await fetch(`${API_URL}/api/auth/guest/`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' }
                                    });
                                    const data = await response.json();
                                    if (!response.ok) throw new Error(data.error || 'Gast-Zugang nicht verfügbar');
                                    login(data.access, data.refresh);
                                    showToast('Klasse! Du bist als Gast dabei. 🎉', 'success');
                                    navigate('/dashboard');
                                } catch (err) {
                                    setError(`Gast-Zugang: ${err.message}`);
                                    showToast(err.message, 'error');
                                } finally { setLoading(false); }
                            }}
                            disabled={loading || oauthLoading.google}
                            className="text-xs font-bold text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-all flex items-center gap-1 group"
                        >
                            Plantlix erst einmal als Gast erkunden
                            <svg className="w-3 h-3 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>

            {/* ── Passwort-vergessen Overlay ── */}
            {forgotPassword && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
                    <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-7 border border-plantlix-200/50 dark:border-plantlix-700/50">
                        {resetSent ? (
                            <div className="text-center">
                                <div className="text-5xl mb-3">📬</div>
                                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">E-Mail gesendet!</h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                                    Falls ein Konto mit dieser E-Mail existiert, erhältst du in Kürze einen Reset-Link.
                                </p>
                                <button
                                    onClick={() => { setForgotPassword(false); setResetSent(false); setResetEmail(''); }}
                                    className="w-full py-3 rounded-lg bg-plantlix-500 hover:bg-plantlix-600 text-white font-semibold transition-all"
                                >
                                    Schließen
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="text-center mb-5">
                                    <div className="text-4xl mb-2">🔑</div>
                                    <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Passwort zurücksetzen</h2>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                        Gib deine E-Mail-Adresse ein. Wir schicken dir einen Reset-Link.
                                    </p>
                                </div>

                                {error && (
                                    <p className="bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-700 p-3 rounded-lg text-center text-sm mb-4">
                                        {error}
                                    </p>
                                )}

                                <form onSubmit={async (e) => {
                                    e.preventDefault();
                                    setError('');
                                    setLoading(true);
                                    try {
                                        const res = await fetch(`${API_URL}/api/password-reset/request/`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ email: resetEmail }),
                                        });
                                        const data = await res.json().catch(() => ({}));
                                        if (!res.ok) throw new Error(data.detail || 'Fehler beim Senden.');
                                        setResetSent(true);
                                    } catch (err) {
                                        setError(err.message);
                                    } finally {
                                        setLoading(false);
                                    }
                                }} className="space-y-4">
                                    <div className="space-y-1">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">E-Mail-Adresse</label>
                                        <input
                                            type="email"
                                            required
                                            value={resetEmail}
                                            onChange={(e) => setResetEmail(e.target.value)}
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-black/20 focus:outline-none focus:ring-2 focus:ring-plantlix-500 transition-all"
                                            placeholder="deine@email.de"
                                            autoFocus
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full py-3 rounded-lg bg-plantlix-500 hover:bg-plantlix-600 text-white font-semibold transition-all disabled:opacity-50"
                                    >
                                        {loading ? (
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto" />
                                        ) : 'Reset-Link senden'}
                                    </button>
                                </form>

                                <button
                                    type="button"
                                    onClick={() => { setForgotPassword(false); setError(''); }}
                                    className="mt-4 w-full text-sm text-gray-500 dark:text-gray-400 hover:text-plantlix-500 transition-colors text-center"
                                >
                                    ← Zurück zur Anmeldung
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Auth;
